// Phase 51-1 Task 2 — typed barrel exposing the 10 studio JPEGs
// as `<picture>`-shaped objects (sources + img) emitted by vite-imagetools.
//
// Each import triggers the imagetools transform at build time:
//   - `w=320;640;1024;1600` → 4 widths
//   - `format=avif;webp;jpg`  → 3 formats per width
//   - `as=picture`            → returns { sources: Record<mime,srcset>, img }
//
// Phase 52 (StudioHero + OG) consumes `studioPictures[heroId]` directly;
// Phase 53 (StudioGallery) iterates `Object.values(studioPictures)`.
//
// Forward-compat: each entry is ALSO re-exported by name (`studio01`..`studio10`)
// per RESEARCH §Open Questions Q4, so consumers can pick either pattern.
//
// Refs: phase 51

import studio01 from './studio-01.jpg?w=320;640;1024;1600&format=avif;webp;jpg&as=picture'
import studio02 from './studio-02.jpg?w=320;640;1024;1600&format=avif;webp;jpg&as=picture'
import studio03 from './studio-03.jpg?w=320;640;1024;1600&format=avif;webp;jpg&as=picture'
import studio04 from './studio-04.jpg?w=320;640;1024;1600&format=avif;webp;jpg&as=picture'
import studio05 from './studio-05.jpg?w=320;640;1024;1600&format=avif;webp;jpg&as=picture'
import studio06 from './studio-06.jpg?w=320;640;1024;1600&format=avif;webp;jpg&as=picture'
import studio07 from './studio-07.jpg?w=320;640;1024;1600&format=avif;webp;jpg&as=picture'
import studio08 from './studio-08.jpg?w=320;640;1024;1600&format=avif;webp;jpg&as=picture'
import studio09 from './studio-09.jpg?w=320;640;1024;1600&format=avif;webp;jpg&as=picture'
import studio10 from './studio-10.jpg?w=320;640;1024;1600&format=avif;webp;jpg&as=picture'

/**
 * Shape of a single studio picture entry as emitted by vite-imagetools
 * `?as=picture` directive. Mirrors imagetools-core `Picture` (re-exported by
 * vite-imagetools v10), but declared locally so consumers don't need to
 * import-types from vite-imagetools and so the contract is self-documenting
 * at the call site.
 */
export interface StudioPicture {
  /**
   * Keyed by MIME format (`avif`, `webp`, `jpeg`). Value is a `<source>`-ready
   * srcset string: `"file-320w.avif 320w, file-640w.avif 640w, …"`.
   */
  sources: Record<string, string>
  /**
   * Fallback `<img>` element data — the largest JPEG variant (1600w).
   */
  img: {
    src: string
    w: number
    h: number
  }
}

/**
 * The 10 studio pictures, keyed by 2-digit ID. Stable key order.
 *
 * Phase 113 (2026-10-01) — '04' + '05' sont ROUVERTS, exactement comme la note
 * precedente l'avait prevu : « operator can re-introduce 04/05 later without
 * renumbering ». Aucun renumerotage n'a ete necessaire, la prevoyance a paye.
 *
 * Les creneaux 02..09 portent desormais les photos du materiel REEL. Les
 * precedentes montraient des bumpers Sveltus + Crossmaxx que le studio n'a plus
 * — et deux fichiers de langue l'affirmaient en toutes lettres sur une page
 * indexee. '01' (le heros, SEULE source paysage 1448x1086) et '10' (la douche)
 * sont INCHANGES : le lot fourni est entierement portrait et ne montre pas la
 * douche.
 */
export const studioPictures = {
  '01': studio01 as StudioPicture,
  '02': studio02 as StudioPicture,
  '03': studio03 as StudioPicture,
  '04': studio04 as StudioPicture,
  '05': studio05 as StudioPicture,
  '06': studio06 as StudioPicture,
  '07': studio07 as StudioPicture,
  '08': studio08 as StudioPicture,
  '09': studio09 as StudioPicture,
  '10': studio10 as StudioPicture,
} as const

/**
 * Union of valid studio IDs. Use this for prop types and lookups, e.g.
 * `function StudioHero({ id }: { id: StudioId })`.
 */
export type StudioId = keyof typeof studioPictures

// Named re-exports for callers that prefer the by-name pattern (Phase 52/53
// forward compat).
export { studio01, studio02, studio03, studio04, studio05, studio06, studio07, studio08, studio09, studio10 }
